// pages/Admin/AdminDashboard.jsx
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Typography } from "antd";
import { Button, Modal, Input, message } from "antd";
import { useState, useEffect, useRef } from "react";
import API from "../../services/api";
import {
  AppstoreOutlined,
  ShopOutlined,
  DeploymentUnitOutlined,
  ApartmentOutlined,
  UserSwitchOutlined,
  LayoutOutlined,
  LineChartOutlined,
  CheckCircleOutlined,
  MenuOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import "./AdminDashboard.css";

const { Title, Paragraph, Text } = Typography;

const adminItems = [
  {
    label: "Manage Users",
    path: "/admin/manage-users",
    icon: <UserSwitchOutlined />,
  },
  {
    label: "Commercial Approvals",
    path: "/admin/commercial-approvals",
    icon: <ShopOutlined />,
  },
  {
    label: "Stall Approvals",
    path: "/admin/stall-approvals",
    icon: <AppstoreOutlined />,
  },
  {
    label: "Franchise Approvals",
    path: "/admin/franchise-approvals",
    icon: <DeploymentUnitOutlined />,
  },
  {
    label: "Business Approvals",
    path: "/admin/business-approvals",
    icon: <ApartmentOutlined />,
  },
  {
    label: "Ads Approvals",
    path: "/admin/ads-approvals",
    icon: <DeploymentUnitOutlined />,
  },
];

function AdminDashboard() {
  const { pathname } = useLocation();

  const activeItem =
    adminItems.find((item) => pathname.startsWith(item.path)) || {
      label: "Dashboard Home",
    };

  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const sidebarRef = useRef(null);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  // Close sidebar on outside click / escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  const sendMarketing = async () => {
    try {
      setLoading(true);
      await API.post("/users/send-marketing", {
        subject,
        message: msg,
      });
      message.success("Marketing emails sent 🚀");
      setOpen(false);
      setSubject("");
      setMsg("");
    } catch (err) {
      message.error("Failed to send emails");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-dashboard__container">

        {/* ── Top Navigation Bar ── */}
        <nav className="dashboard-nav">
          {/* Hamburger — visible on ≤900px */}
          <button
            className="dashboard-nav__hamburger"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label={sidebarOpen ? "Close menu" : "Open menu"}
          >
            {sidebarOpen
              ? <><span style={{ transform: "rotate(45deg) translate(5px, 5px)" }} /><span style={{ opacity: 0 }} /><span style={{ transform: "rotate(-45deg) translate(5px, -5px)" }} /></>
              : <><span /><span /><span /></>
            }
          </button>

          <div className="dashboard-nav__brand">
            <LayoutOutlined className="dashboard-nav__logo" />
            <span className="dashboard-nav__title">Admin Management Panel</span>
          </div>

          <div className="dashboard-nav__actions">
            <Button
              type="primary"
              onClick={() => setOpen(true)}
              className="dashboard-nav__cta"
            >
              Send Marketing Email
            </Button>
          </div>
        </nav>

        {/* ── Main Content ── */}
        <div className="dashboard-main">

          {/* Overlay — closes sidebar on tap */}
          <div
            className={`dashboard-sidebar__overlay ${sidebarOpen ? "is-visible" : ""}`}
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />

          {/* Sidebar */}
          <aside
            ref={sidebarRef}
            className={`dashboard-sidebar ${sidebarOpen ? "is-open" : ""}`}
          >
            <div className="dashboard-sidebar__header">
              <Text className="dashboard-sidebar__greeting">Welcome back</Text>
              <Text className="dashboard-sidebar__name">Administrator</Text>
            </div>

            <div className="dashboard-sidebar__nav">
              {adminItems.map((item, index) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `dashboard-sidebar__item ${isActive ? "dashboard-sidebar__item--active" : ""}`
                  }
                >
                  <span className="dashboard-sidebar__icon">{item.icon}</span>
                  <span className="dashboard-sidebar__label">{item.label}</span>
                  <span className="dashboard-sidebar__index">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </NavLink>
              ))}
            </div>

            <div className="dashboard-sidebar__footer">
              <div className="dashboard-sidebar__stat">
                <LineChartOutlined />
                <div>
                  <Text className="dashboard-sidebar__stat-value">
                    {adminItems.length}
                  </Text>
                  <Text className="dashboard-sidebar__stat-label">
                    Active Modules
                  </Text>
                </div>
              </div>
            </div>
          </aside>

          {/* Content Area */}
          <main className="dashboard-content">
            {/* Page Header */}
            <div className="dashboard-content__header">
              <div className="dashboard-content__title-section">
                <div className="dashboard-content__badge">
                  <CheckCircleOutlined />
                  <span>Active Panel</span>
                </div>
                <Title level={1} className="dashboard-content__title">
                  {activeItem.label}
                </Title>
                <Paragraph className="dashboard-content__description">
                  Manage and monitor all platform activities from this centralized dashboard
                </Paragraph>
              </div>

              <div className="dashboard-content__stats">
                <div className="dashboard-stats-card">
                  <Text className="dashboard-stats-card__label">Total Modules</Text>
                  <Text className="dashboard-stats-card__value">
                    {adminItems.length}
                  </Text>
                </div>
              </div>
            </div>

            {/* Outlet Content */}
            <div className="dashboard-content__body">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {/* Modal */}
      <Modal
        title="Send Marketing Email"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={sendMarketing}
        confirmLoading={loading}
        className="admin-modal"
        okText="Send Email"
        cancelText="Cancel"
      >
        <div className="admin-modal__form">
          <div className="admin-modal__field">
            <label className="admin-modal__label">Subject Line</label>
            <Input
              placeholder="e.g., Special Announcement for All Users"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              size="large"
            />
          </div>

          <div className="admin-modal__field">
            <label className="admin-modal__label">Message Content</label>
            <Input.TextArea
              rows={6}
              placeholder="Write your message here..."
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              size="large"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default AdminDashboard;